<!--
  ~ Copyright (c) GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->
<fab-loading-spinner color="primary" *ngIf="showSpinner"
                     id="loading-spinner"></fab-loading-spinner>
<div id="header-container">
  <h3 id="header-text" class="align-self-end pb-1">Vehicle Data for Pivot Table</h3>
  <div class="d-flex gap-8 pb-1 align-items-center">
    <button mat-flat-button color="primary" id="run-report-button" class="action-button"
            (click)="onRunReport()"
            [disabled]="!isButtonEnabled()">
      Run Report
    </button>
  </div>
</div>

<div id="main-content" class="d-flex gap-16 align-content-between">
  <div id="filters" class="filter-container d-flex flex-column gap-8">
    <!--    list of dropdowns -->
    <h3>Filter By:</h3>
    <mat-form-field subscriptSizing="dynamic" *ngIf="!(selectedScenarioSource === 1)">
      <mat-label>Geography</mat-label>
      <mat-select [(ngModel)]="subregionFlag" id="geographyFilter"
                  (selectionChange)="onGeographyChange()">
        <mat-option [value]="0">Market Area</mat-option>
        <mat-option [value]="1">Subregion</mat-option>
        <mat-option [value]="2">Planning Market</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field *ngIf="subregionFlag === 0" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">Market Area</mat-label>
      <mat-select [(ngModel)]="marketAreas.selectedOptions" id="mktAreaFilter"
                  [disabled]="marketAreas.options.length === 0">
        <mat-option *ngFor="let mktArea of marketAreas.options" [value]="[mktArea]">{{ mktArea }}</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field *ngIf="subregionFlag === 1 && !(selectedScenarioSource === 1)" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">Subregion</mat-label>
      <fab-multiselect
        name="subregionFilter"
        [(ngModel)]="subregions.selectedOptions"
        [options]="subregions.options"
        [disabled]="subregions.options.length === 0">
      </fab-multiselect>
    </mat-form-field>
    <mat-form-field *ngIf="subregionFlag === 2" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">Planning Markets</mat-label>
      <fab-multiselect
        name="planningMarketFilter"
        [(ngModel)]="planningMarkets.selectedOptions"
        [options]="planningMarkets.options"
        [disabled]="planningMarkets.options.length === 0">
      </fab-multiselect>
    </mat-form-field>
    <div class="d-flex gap-8 flex-row justify-content-center ">
      <mat-form-field subscriptSizing="dynamic">
        <mat-label class="asterisk--before">Start Year</mat-label>
        <mat-select [(ngModel)]="startYear" id="startYear">
          <mat-option *ngFor="let year of yearRange" [value]="year">{{ year }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field subscriptSizing="dynamic">
        <mat-label class="asterisk--before">End Year</mat-label>
        <mat-select [(ngModel)]="endYear" id="endYear">
          <mat-option *ngFor="let year of yearRange" [value]="year">{{ year }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <mat-radio-group id="detail-level" class="d-flex flex-column" [(ngModel)]="getPortLevelData">
      <mat-radio-button [value]="1">
        By Portfolio Level Detail
      </mat-radio-button>
      <mat-radio-button [value]="2">
        By Brand Family
      </mat-radio-button>
    </mat-radio-group>
    <mat-radio-group id="segment-level" class="d-flex flex-column" [(ngModel)]="getPropulsionSegment">
      <mat-label>Industry Segment Level</mat-label>
      <mat-radio-button [value]="false">
        Industry - Total Vehicle
      </mat-radio-button>
      <mat-radio-button [value]="true">
        Industry - Propulsion Segment
      </mat-radio-button>
    </mat-radio-group>
    <mat-checkbox id="compare-check" [(ngModel)]="includeCompare" (change)="onCompareChange()">Include Compare
      Scenario
    </mat-checkbox>
  </div>
  <div id="scenario-content" class="d-flex flex-column gap-8 w-100">
    <div class="d-flex flex-column justify-content-center">
      <mat-button-toggle-group [(ngModel)]="selectedScenarioSource"
                               hideSingleSelectionIndicator="true"
                               (ngModelChange)="onSourceChange()">
        <mat-button-toggle id="forecast-org-toggle" [value]="0" class="flex-grow-1">
          By Forecast Org
        </mat-button-toggle>
        <mat-button-toggle id="global-scenario-toggle" [value]="1" class="flex-grow-1">
          By Global Scenario
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>
    <div id="scenario-container" class="d-flex w-100" *ngIf="selectedScenarioSource === 0">
      <table id="scenario-info-table" class="w-100">
        <tr>
          <td></td>
          <td class="working-scenario-content-border working-scenario-content-border-top column-header">
            <h2><b>Working Scenario</b></h2>
            <h3 *ngIf="!selectedScenario" style="font-style: italic">Select Scenario
              Below</h3>
          </td>
          <td class="column-header">
            <h2 [ngClass]="{'disabled-text': !selectedScenario}"><b>Compare Scenario</b></h2>
          </td>
        </tr>

        <!-- FORECAST ORG ROW -->
        <tr>
          <td class="scenario-labels">Forecast Organization</td>
          <td *ngIf="selectedScenario" class="working-scenario-content-border">
            <div class="column-content">
              <span class="column-text">{{ selectedScenario.fcorg }}</span>
            </div>
          </td>
          <td *ngIf="compareScenario">
            <div class="column-content">
              <span class="column-text">{{ compareScenario.fcorg }}</span>
            </div>
          </td>
          <!-- if no selected scenarios -->
          <td *ngIf="!selectedScenario" class="working-scenario-content-border column-content"></td>
          <td *ngIf="!compareScenario"></td>
        </tr>

        <!-- VEHICLE SCENARIO ROW -->
        <tr class="light-gray-background">
          <td class="scenario-labels">Vehicle Scenario</td>
          <td *ngIf="selectedScenario"
              class="working-scenario-content-border">
            <div class="column-content">
              <span class="column-text pad-left-50">{{ selectedScenario.vehScenId }} - {{ selectedScenario.vehScenName }}</span>
              <button mat-icon-button [matMenuTriggerFor]="vehicleMenu" class="vertical-dots"
                      matTooltip="More options">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #vehicleMenu="matMenu">
                <button mat-menu-item (click)="selectScenario()" class="menu-width">
                  <mat-icon>autorenew</mat-icon>
                  <span>Change Scenario</span>
                </button>
              </mat-menu>
            </div>
          </td>
          <td *ngIf="compareScenario">
            <div class="column-content">
              <span class="column-text pad-left-50">{{ compareScenario.vehScenId }} - {{ compareScenario.vehScenName }}</span>
              <button mat-icon-button [matMenuTriggerFor]="compareVehicleMenu" class="vertical-dots"
                      matTooltip="More options">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #compareVehicleMenu="matMenu">
                <button mat-menu-item (click)="selectCompareScenario()" class="menu-width">
                  <mat-icon>autorenew</mat-icon>
                  <span>Change Scenario</span>
                </button>
              </mat-menu>
            </div>
          </td>
          <!-- if no selected scenarios -->
          <td *ngIf="!selectedScenario" class="working-scenario-content-border column-content">
          <span class="hyperlink"
                tabindex="0"
                (click)="selectScenario()"
                (keydown.enter)="selectScenario()">Select Vehicle Scenario</span>
          </td>
          <td *ngIf="!compareScenario"></td>
        </tr>

        <!-- INDUSTRY SCENARIO ROW -->
        <tr>
          <td class="scenario-labels">Industry Scenario</td>
          <td *ngIf="selectedScenario"
              class="working-scenario-content-border working-scenario-content-border-bottom">
            <div class="column-content">
              <span class="column-text">{{ selectedScenario.industryScenario.id }} - {{ selectedScenario.industryScenario.industryScenarioName }}</span>
            </div>
          </td>
          <td *ngIf="compareScenario">
            <div class="column-content">
              <span class="column-text">{{ compareScenario.industryScenario.id }} - {{ compareScenario.industryScenario.industryScenarioName }}</span>
            </div>
          </td>
          <!-- if no selected scenarios -->
          <td *ngIf="!selectedScenario"
              class="working-scenario-content-border working-scenario-content-border-bottom column-content"></td>
          <td *ngIf="!compareScenario"></td>
        </tr>

      </table>
    </div>
    <div *ngIf="selectedScenarioSource === 1" class="content-height d-flex flex-column w-100 gap-8">
      <div class="d-flex flex-column flex-table">
        <h3>Working Global Scenario</h3>
        <app-global-scenario-select-component
          class="d-flex h-100"
          [user]="stateService.getUserGmid()"
          [memberScenarioType]="0"
          (confirmEvent)="onGlobalScenarioSelect($event)">
        </app-global-scenario-select-component>
      </div>

      <mat-divider *ngIf="includeCompare"></mat-divider>
      <div class="d-flex flex-column flex-table" *ngIf="includeCompare">
        <h3>Compare Global Scenario</h3>
        <app-global-scenario-select-component
          class="d-flex h-100"
          *ngIf="includeCompare"
          [user]="stateService.getUserGmid()"
          [memberScenarioType]="0"
          (confirmEvent)="onCompareGlobalScenarioSelect($event)">
        </app-global-scenario-select-component>
      </div>
    </div>
  </div>
</div>
